import React from 'react';
import { Clock, Trash2, Edit, Play } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент отображения сохраненных таймеров
 */
const SavedTimers = ({ 
  savedTimers, 
  loadTimer, 
  deleteTimer,
  activeTimerId
}) => {
  // Если нет сохраненных таймеров
  if (!savedTimers || savedTimers.length === 0) {
    return null;
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-500" />
          Сохраненные таймеры
        </h2>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedTimers.length} {getTimersCountText(savedTimers.length)}
        </span>
      </div>
      
      <div className="overflow-hidden">
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">Название</th>
                <th scope="col" className="px-4 py-3 font-semibold">Тип</th>
                <th scope="col" className="px-4 py-3 font-semibold">Дата/Время</th>
                <th scope="col" className="px-4 py-3 font-semibold text-right">Действия</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {savedTimers.map((timer) => (
                <tr 
                  key={timer.id}
                  className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100
                    ${activeTimerId === timer.id ? 'bg-indigo-50 dark:bg-indigo-900/20' : ''}`}
                >
                  <td className="px-4 py-2 whitespace-nowrap font-medium">
                    {timer.timerName || 'Без названия'}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {getTimerTypeText(timer)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {formatTimerTarget(timer)}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Play className="w-4 h-4 text-green-500" />}
                        title="Загрузить и запустить"
                        onClick={() => loadTimer(timer.id)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Edit className="w-4 h-4 text-indigo-500" />}
                        title="Редактировать"
                        onClick={() => loadTimer(timer.id)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={<Trash2 className="w-4 h-4 text-red-500" />}
                        title="Удалить"
                        onClick={() => {
                          if (window.confirm(`Вы уверены, что хотите удалить таймер "${timer.timerName || 'Без названия'}"?`)) {
                            deleteTimer(timer.id);
                          }
                        }}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слова "таймер"
const getTimersCountText = (count) => {
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
    return 'таймеров';
  }
  
  if (lastDigit === 1) {
    return 'таймер';
  }
  
  if (lastDigit >= 2 && lastDigit <= 4) {
    return 'таймера';
  }
  
  return 'таймеров';
};

// Получение текстового описания типа таймера
const getTimerTypeText = (timer) => {
  if (timer.timerMode === 'date') {
    return timer.timerType === 'countdown' ? 'Обратный отсчёт до даты' : 'Прямой отсчёт от даты';
  } else {
    return timer.timerType === 'countdown' ? 'Обратный таймер' : 'Прямой таймер';
  }
};

// Форматирование целевой даты или времени таймера
const formatTimerTarget = (timer) => {
  if (timer.timerMode === 'date') {
    const date = new Date(`${timer.targetDate}T${timer.targetTime || '00:00'}:00`);
    return date.toLocaleString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } else {
    return `${timer.timerHours}ч ${timer.timerMinutes}м ${timer.timerSeconds}с`;
  }
};

export default SavedTimers;